import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ClipboardList, FileText, Loader2, Search, ShieldAlert, ShoppingBag, Users, Wallet, X } from "lucide-react";
import api from "../api/api";
import EmployeeInsightsDrawer from "./EmployeeInsightsDrawer";

const CATEGORY_ICON = {
  tasks: ClipboardList,
  expenses: Wallet,
  users: Users,
  crm: Building2,
  purchase_orders: ShoppingBag,
  work_reports: FileText,
};

const CATEGORY_LABEL = {
  tasks: "Tasks",
  expenses: "Expenses",
  users: "People",
  crm: "CRM",
  purchase_orders: "Purchase Orders",
  work_reports: "Work Reports",
};

const CATEGORY_ORDER = ["users", "tasks", "expenses", "purchase_orders", "work_reports", "crm"];
const ID_QUERY_RE = /^(?:\d+|TPD[\s-]*(?:EXP[\s-]*)?\d+)$/i;
const RAW_ID_RE = /^\d+$/;

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timeoutId);
  }, [value, delay]);

  return debounced;
}

function isIdLikeSearch(value) {
  return ID_QUERY_RE.test(value.trim());
}

function formatStatus(status) {
  return status?.replace(/_/g, " ");
}

function normalizeSearchCode(value) {
  return String(value || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
}

function formatCurrency(value) {
  return `Rs. ${Number(value || 0).toLocaleString("en-IN")}`;
}

function getExactMatches(results, rawQuery) {
  const normalizedQuery = normalizeSearchCode(rawQuery);
  const parsedNumber = RAW_ID_RE.test(rawQuery.trim()) ? Number.parseInt(rawQuery.trim(), 10) : null;
  const taskItems = results?.results?.tasks || [];
  const expenseItems = results?.results?.expenses || [];

  const tasks = taskItems.filter((item) => {
    const normalizedCode = normalizeSearchCode(item.task_code);
    return normalizedCode === normalizedQuery || (Number.isFinite(parsedNumber) && item.task_no === parsedNumber);
  });

  const expenses = expenseItems.filter((item) => {
    const normalizedCode = normalizeSearchCode(item.expense_code);
    return normalizedCode === normalizedQuery || (Number.isFinite(parsedNumber) && item.expense_no === parsedNumber);
  });

  return { tasks, expenses };
}

function getDirectMatch(results, rawQuery) {
  if (!results || !isIdLikeSearch(rawQuery)) {
    return null;
  }

  const normalizedQuery = normalizeSearchCode(rawQuery);
  const { tasks, expenses } = getExactMatches(results, rawQuery);

  if (normalizedQuery.startsWith("TPDEXP")) {
    return expenses[0] || null;
  }

  if (normalizedQuery.startsWith("TPD")) {
    return tasks[0] || null;
  }

  return tasks.length + expenses.length === 1 ? tasks[0] || expenses[0] || null : null;
}

export default function GlobalSearch() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const dropRef = useRef(null);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [insightsEmployee, setInsightsEmployee] = useState(null);

  const debounced = useDebounce(query, 300);

  useEffect(() => {
    const trimmed = debounced.trim();

    if (!trimmed || (!isIdLikeSearch(trimmed) && trimmed.length < 3)) {
      setResults(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);

    api
      .get(`/search/?q=${encodeURIComponent(trimmed)}`, { signal: controller.signal })
      .then((response) => {
        setResults(response.data);
        setOpen(true);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          setResults(null);
        }
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [debounced]);

  useEffect(() => {
    const handler = (event) => {
      if (!inputRef.current?.contains(event.target) && !dropRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const go = (item) => {
    navigate(item.url, { state: { searchFocusAt: Date.now() } });
    setOpen(false);
    setQuery("");
  };

  // Employee results open an insights drawer instead of navigating straight to
  // the generic Users page — "Manage User" inside the drawer covers that case.
  const openInsights = (item) => {
    setInsightsEmployee(item);
    setOpen(false);
    setQuery("");
  };

  const messages = results?.messages || [];
  const hasResults = Boolean(results && results.total > 0);
  const hasMessages = messages.length > 0;
  const directMatch = getDirectMatch(results, query);
  const categories = results
    ? Object.entries(results.results)
        .filter(([, items]) => Array.isArray(items) && items.length > 0)
        .sort(([left], [right]) => CATEGORY_ORDER.indexOf(left) - CATEGORY_ORDER.indexOf(right))
    : [];

  return (
    <div className="relative w-full max-w-none sm:max-w-2xl">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          autoComplete="off"
          spellCheck={false}
          aria-label="Search tasks, expenses, users, and IDs"
          placeholder="Search IDs, tasks, expenses, users..."
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            if (results) {
              setOpen(true);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              const match = getDirectMatch(results, event.currentTarget.value);
              if (match) {
                event.preventDefault();
                go(match);
                return;
              }
            }
            if (event.key === "Escape") {
              setOpen(false);
              inputRef.current?.blur();
            }
          }}
          className="h-10 w-full rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 py-2 pl-9 pr-10 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none transition focus:border-slate-300 dark:focus:border-slate-600 focus:bg-white dark:focus:bg-slate-900 focus:ring-2 focus:ring-sky-500/20"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400 dark:text-slate-500" />
        )}
        {!loading && query && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setResults(null);
              setOpen(false);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 transition hover:text-slate-600 dark:hover:text-slate-300"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && query.trim() && (
        <div
          ref={dropRef}
          className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[70vh] overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl sm:max-h-[26rem]"
        >
          {loading && <div className="px-4 py-4 text-center text-xs text-slate-400">Searching...</div>}

          {!loading && directMatch && (
            <button
              type="button"
              onClick={() => go(directMatch)}
              className="flex w-full items-center justify-between gap-3 border-b border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-left transition hover:bg-emerald-500/15"
            >
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-300">
                  Exact Match
                </p>
                <p className="truncate text-sm font-medium text-slate-100">
                  {directMatch.display_id || directMatch.task_code || directMatch.expense_code || directMatch.title}
                </p>
              </div>
              <span className="flex-shrink-0 text-xs font-medium text-emerald-200">
                Press Enter to open
              </span>
            </button>
          )}

          {!loading && hasMessages && (
            <div className="border-b border-slate-800 bg-rose-500/5 px-3 py-3">
              {messages.map((message, index) => (
                <div
                  key={`${message.type || "message"}-${index}`}
                  className="mb-2 rounded-xl border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-xs text-rose-100 last:mb-0"
                >
                  <div className="flex items-start gap-2">
                    <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-300" />
                    <span>{message.text}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && !hasResults && !hasMessages && results !== null && (
            <div className="px-4 py-6 text-center text-xs text-slate-400">
              No results for <strong className="text-slate-200">"{query}"</strong>
            </div>
          )}

          {!loading &&
            hasResults &&
            categories.map(([category, items]) => {
              const Icon = CATEGORY_ICON[category] || Building2;
              return (
                <div key={category} className="border-b border-slate-800 last:border-b-0">
                  <div className="flex items-center gap-2 border-b border-slate-800 bg-slate-900/70 px-3 py-2">
                    <Icon className="h-4 w-4 text-slate-400" />
                    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                      {CATEGORY_LABEL[category] || category}
                    </span>
                    <span className="ml-auto text-xs text-slate-500">
                      {items.length} result{items.length !== 1 ? "s" : ""}
                    </span>
                  </div>

                  {items.map((item) => (
                    <button
                      key={`${category}-${item.id}`}
                      type="button"
                      onClick={() => (category === "users" ? openInsights(item) : go(item))}
                      className="w-full border-b border-slate-900/70 px-4 py-3 text-left transition hover:bg-slate-900/80 last:border-b-0"
                    >
                      {category === "tasks" && (
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            {(item.display_id || item.task_code) && (
                              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                {item.display_id || item.task_code}
                              </p>
                            )}
                            <p className="truncate text-sm font-medium text-slate-100">{item.title}</p>
                          </div>
                          <span className="flex-shrink-0 rounded-full bg-sky-500/10 px-2 py-0.5 text-[11px] capitalize text-sky-300">
                            {formatStatus(item.status)}
                          </span>
                        </div>
                      )}

                      {category === "expenses" && (
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            {(item.display_id || item.expense_code) && (
                              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                {item.display_id || item.expense_code}
                              </p>
                            )}
                            <p className="truncate text-sm font-medium text-slate-100">{item.title}</p>
                          </div>
                          <div className="flex flex-shrink-0 items-center gap-2">
                            <span className="text-sm font-semibold text-slate-200">
                              {formatCurrency(item.amount)}
                            </span>
                            <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[11px] capitalize text-slate-300">
                              {formatStatus(item.status)}
                            </span>
                          </div>
                        </div>
                      )}

                      {category === "users" && (
                        <div className="flex items-start gap-3">
                          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-slate-800 text-sm font-bold text-white">
                            {(item.full_name || "?").charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-slate-100">{item.full_name}</p>
                                <p className="truncate text-xs text-slate-400">{item.email}</p>
                              </div>
                              <span className="flex-shrink-0 rounded-full bg-slate-800 px-2 py-0.5 text-[11px] capitalize text-slate-300">
                                {formatStatus(item.role)}
                              </span>
                            </div>
                            <p className="mt-1 truncate text-xs text-slate-500">
                              {[item.designation, item.department].filter(Boolean).join(" | ") || "No department assigned"}
                            </p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span className="rounded-full border border-indigo-500/30 bg-indigo-500/10 px-2 py-0.5 text-[11px] text-indigo-300">
                                In Progress {item.kpi?.in_progress || 0}
                              </span>
                              <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-300">
                                Pending {item.kpi?.pending || 0}
                              </span>
                              <span className="rounded-full border border-rose-500/30 bg-rose-500/10 px-2 py-0.5 text-[11px] text-rose-300">
                                Overdue {item.kpi?.overdue || 0}
                              </span>
                              <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-300">
                                Completed {item.kpi?.completed || 0}
                              </span>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[11px] text-slate-300">
                                Expenses {item.expense_total || 0} · {formatCurrency(item.expense_amount)}
                              </span>
                            </div>
                            {(item.recent_tasks?.length > 0 || item.recent_expense_codes?.length > 0) && (
                              <div className="mt-2 space-y-2">
                                {item.recent_tasks?.length > 0 && (
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                                      Recent Tasks
                                    </span>
                                    {item.recent_tasks.map((task) => (
                                      <span
                                        key={`${item.id}-task-${task.id}`}
                                        className="rounded-full border border-sky-500/20 bg-sky-500/10 px-2 py-0.5 text-[11px] font-medium text-sky-200"
                                      >
                                        {task.task_code}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {item.recent_expense_codes?.length > 0 && (
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                                      Recent Expenses
                                    </span>
                                    {item.recent_expense_codes.map((code) => (
                                      <span
                                        key={`${item.id}-expense-${code}`}
                                        className="rounded-full border border-amber-500/20 bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-200"
                                      >
                                        {code}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {category === "crm" && (
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            {item.display_id && (
                              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                {item.display_id}
                              </p>
                            )}
                            <p className="truncate text-sm font-medium text-slate-100">{item.customer_name}</p>
                            {item.company_name && (
                              <p className="truncate text-xs text-slate-400">{item.company_name}</p>
                            )}
                          </div>
                          <span className="flex-shrink-0 rounded-full bg-slate-800 px-2 py-0.5 text-[11px] capitalize text-slate-300">
                            {formatStatus(item.status)}
                          </span>
                        </div>
                      )}

                      {category === "purchase_orders" && (
                        <div className="min-w-0">
                          {item.display_id && (
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                              {item.display_id}
                            </p>
                          )}
                          <p className="truncate text-sm font-medium text-slate-100">{item.po_number}</p>
                        </div>
                      )}

                      {category === "work_reports" && (
                        <div className="min-w-0">
                          {item.display_id && (
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                              {item.display_id}
                            </p>
                          )}
                          <p className="truncate text-sm font-medium text-slate-100">{item.report_date}</p>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              );
            })}

          {hasResults && (
            <div className="border-t border-slate-800 bg-slate-950 px-4 py-2 text-center">
              <p className="text-xs text-slate-400">
                {results.total} result{results.total !== 1 ? "s" : ""} found
              </p>
            </div>
          )}
        </div>
      )}

      {insightsEmployee && (
        <EmployeeInsightsDrawer employee={insightsEmployee} onClose={() => setInsightsEmployee(null)} />
      )}
    </div>
  );
}